import React, {useEffect, useState} from 'react'
import styles from './quantitySelector.module.scss'

export type QuantitySelectorProps = {
  value: number
  onChange: (next: number) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
}

export const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  disabled = false,
}) => {
  const atMin = value <= min
  const atMax = max !== undefined && value >= max

  // Local draft so the field can be cleared and retyped without the controlled
  // value snapping mid-keystroke. Re-synced whenever `value` changes externally
  // (the +/− buttons, or the parent's server/local reconciliation).
  const [draft, setDraft] = useState(String(value))
  useEffect(() => {
    setDraft(String(value))
  }, [value])

  const clamp = (candidate: number): number => {
    if (candidate < min) return min
    if (max !== undefined && candidate > max) return max
    return candidate
  }

  // Both steps clamp: a value sitting BELOW min (a legacy line under a floor
  // raised later) must jump up to the floor on +, never land in between.
  const handleDecrement = () => {
    if (!disabled && !atMin) {
      onChange(clamp(value - step))
    }
  }

  const handleIncrement = () => {
    if (!disabled && !atMax) {
      onChange(clamp(value + step))
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    // Digits only; allow an empty field while typing.
    if (raw !== '' && !/^\d+$/.test(raw)) return
    setDraft(raw)
    if (raw === '') return
    const clamped = clamp(parseInt(raw, 10))
    if (clamped !== value) {
      onChange(clamped)
    }
  }

  const handleBlur = () => {
    const parsed = parseInt(draft, 10)
    if (Number.isNaN(parsed)) {
      // Empty / invalid — revert to the last committed value.
      setDraft(String(value))
      return
    }
    const clamped = clamp(parsed)
    setDraft(String(clamped))
    if (clamped !== value) {
      onChange(clamped)
    }
  }

  const containerClass = [
    styles['quantity-selector'],
    'flx-a-c',
    'border-xxl',
    'bg-cream',
    disabled ? `cursor-not-allowed ${styles['quantity-selector--disabled']}` : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={containerClass} role="group" aria-label="quantity selector">
      <button
        type="button"
        className={`row-flx flx-a-c flx-j-c itm-flx-s-0 txt-kale ${styles['quantity-selector__btn']}`}
        onClick={handleDecrement}
        disabled={disabled || atMin}
        aria-label="decrement"
      >
        {'−'}
      </button>
      <input
        type="text"
        inputMode="numeric"
        className={`p-v-0 p-h-4 border-0 bg-transparent txt-a-c txt-bold-500 txt-size-16 txt-kale ${styles['quantity-selector__value']}`}
        value={draft}
        onChange={handleInputChange}
        onBlur={handleBlur}
        disabled={disabled}
        aria-label="quantity"
      />
      <button
        type="button"
        className={`row-flx flx-a-c flx-j-c itm-flx-s-0 txt-kale ${styles['quantity-selector__btn']}`}
        onClick={handleIncrement}
        disabled={disabled || atMax}
        aria-label="increment"
      >
        +
      </button>
    </div>
  )
}
